import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import { useQuestionnaire } from '../CustomHooks/useQuestionnaire';

export default function EditQuestionnaireModal({ open, setOpen, data }) {
  const onClose = () => setOpen(false);

  const {
    questionnaire,
    accountDetails,
    handleQuestionnaireChange,
    handleAccountChange,
    onUpdate,
    vatRate,
    setVatRates,
    otherVatRate,
    setOtherVatRate,
  } = useQuestionnaire({ data });

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      fullWidth
      maxWidth="lg">
      {/* Header */}
      <DialogTitle>Edit Contract Questionnaire</DialogTitle>

      {/* Body (empty) */}
      <DialogContent>
        <Box sx={{ mt: 2, p: 3, border: '1px solid #ccc', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Questionnaire
          </Typography>

          {/* ======================= */}
          {/* 1️⃣ Thinning & Clear-fell */}
          {/* ======================= */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.firstThinnings}
                      onChange={e => handleQuestionnaireChange('firstThinnings', e.target.checked)}
                    />
                  }
                  label="1st Thinning's (trees left 1500-1700)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.secondThinnings}
                      onChange={e => handleQuestionnaireChange('secondThinnings', e.target.checked)}
                    />
                  }
                  label="2nd Thinning's (trees left 1000-1200)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.clearFell}
                      onChange={e => handleQuestionnaireChange('clearFell', e.target.checked)}
                    />
                  }
                  label="Clear-fell"
                />
              </FormGroup>
              <TextField
                fullWidth
                label="Trees left per hectare (if different)"
                value={questionnaire.treesLeftPerHectareIfDiff}
                onChange={e => handleQuestionnaireChange('treesLeftPerHectareIfDiff', e.target.value)}
              />
              <TextField
                fullWidth
                label="Note: If you need different quantities left please state"
                value={questionnaire.noteIfYouNeedDifferentQtys}
                onChange={e => handleQuestionnaireChange('noteIfYouNeedDifferentQtys', e.target.value)}
                sx={{ marginTop: '1rem' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Environmental Areas
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.archaeologicalArea}
                      onChange={e => handleQuestionnaireChange('archaeologicalArea', e.target.checked)}
                    />
                  }
                  label="Archaeological area"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.recreationalArea}
                      onChange={e => handleQuestionnaireChange('recreationalArea', e.target.checked)}
                    />
                  }
                  label="Recreational area"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.waterways}
                      onChange={e => handleQuestionnaireChange('waterways', e.target.checked)}
                    />
                  }
                  label="Waterways"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.powerOrPhoneLines}
                      onChange={e => handleQuestionnaireChange('powerOrPhoneLines', e.target.checked)}
                    />
                  }
                  label="Power/phone lines"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.railLines}
                      onChange={e => handleQuestionnaireChange('railLines', e.target.checked)}
                    />
                  }
                  label="Rail lines"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.nearbyDwellings}
                      onChange={e => handleQuestionnaireChange('nearbyDwellings', e.target.checked)}
                    />
                  }
                  label="Nearby dwellings"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionnaire.otherPleaseDetail}
                      onChange={e => handleQuestionnaireChange('otherPleaseDetail', e.target.checked)}
                    />
                  }
                  label="Other (please detail)"
                />
              </FormGroup>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
            <Box flex={1}>
              <FormLabel>Do you require a hard copy to be left in the forest?</FormLabel>
              <RadioGroup
                row
                value={questionnaire.doYouRequireHardCopy}
                onChange={e => handleQuestionnaireChange('doYouRequireHardCopy', e.target.value)}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box flex={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box flex={1}>
                <FormLabel>Is permit Doc required?</FormLabel>
                <RadioGroup
                  row
                  value={questionnaire.isPermitDocRequired}
                  onChange={e => handleQuestionnaireChange('isPermitDocRequired', e.target.value)}>
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
              <Box flex={1}>
                <FormLabel>Is Supplier Doc required?</FormLabel>
                <RadioGroup
                  row
                  value={questionnaire.isSupplierDocRequired}
                  onChange={e => handleQuestionnaireChange('isSupplierDocRequired', e.target.value)}>
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
            </Box>
          </Box>
          <Box flex={1} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormLabel style={{ marginRight: '0.75rem' }}>VAT Rates?</FormLabel>
            <RadioGroup
              row
              value={vatRate}
              onChange={e => {
                const val = e.target.value;
                if (val === '0') {
                  setOtherVatRate(true);
                  setVatRates('');
                } else {
                  setVatRates(val);
                }
              }}>
              <FormControlLabel value="5" control={<Radio />} label="Farmer 5.0%" />
              <FormControlLabel value="23" control={<Radio />} label="23%" />
              <FormControlLabel value="0" control={<Radio />} label="Other" />
            </RadioGroup>

            {otherVatRate && (
              <TextField
                label="Enter VAT rate"
                type="number"
                size="small"
                value={vatRate}
                onChange={e => setVatRates(e.target.value)}
                sx={{ mt: 1, width: 150 }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2, p: 3, border: '1px solid #ccc', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            Account Details (for payments)
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap', // keeps layout responsive on smaller screens
            }}>
            <TextField
              label="Account Holder"
              variant="outlined"
              size="small"
              value={accountDetails.accountHolderName}
              onChange={e => handleAccountChange('accountHolderName', e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />

            <TextField
              label="Bank Name"
              variant="outlined"
              size="small"
              value={accountDetails.bankName}
              onChange={e => handleAccountChange('bankName', e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />

            <TextField
              label="IBAN"
              variant="outlined"
              size="small"
              value={accountDetails.iban}
              onChange={e => handleAccountChange('iban', e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />

            <TextField
              label="BIC"
              variant="outlined"
              size="small"
              value={accountDetails.bic}
              onChange={e => handleAccountChange('bic', e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={onUpdate} variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
